// native
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

// addon
import { asBib, asRis } from '@readcube/rcp-citation-export';

// service
import { ApiService } from './api.service';
import { GlobalsService } from './globals.service';
import { UtilityService } from './utility.service';
import { ScreenService } from './screen.service';

// models
import { IDisplayItem, IItem, IList, IListCreateRequest, IAddToListRequest } from '../../models';

// constants
import { BULK_REQUEST_SIZE } from '../../constants/constants';

@Injectable({
  providedIn: 'root'
})
export class ActiveReferencesService {

  areActiveReferencesShown: boolean = false;
  createListLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private apiService: ApiService,
    private globalsService: GlobalsService,
    private utilityService: UtilityService,
    private screenService: ScreenService
  ) { }

  addList(items: IDisplayItem[], name: string, collectionId: string, parentListId: string): Observable<IList[]> {
    const url = `${this.globalsService.appConfig.environment.baseUrls.sync}/collections/${collectionId}/lists`;

    const body: IListCreateRequest = {
      name,
      collection_id: collectionId
    };

    if (parentListId)
      body.parent_id = parentListId;

    return this.apiService.post(url, body).pipe(
      switchMap((res: { list: IList; }) => from(this.addToList(res.list.collection_id, res.list.id, this.extractItemIds(items))))
    );
  }

  async addToList(collectionId: string, listId: string, itemIds: string[]): Promise<IList[]> {
    const url = `${this.globalsService.appConfig.environment.baseUrls.sync}/smartcite/add_to_list`;

    const jobs: (() => Promise<IList[]>)[] = this.utilityService.chunkify(itemIds, BULK_REQUEST_SIZE).map((batch: string[]) => () => {

      const body: IAddToListRequest = {
        list_id: listId,
        collection_id: collectionId,
        ids: batch
      };

      return this.apiService.post(url, body).toPromise() as Promise<IList[]>;
    });

    return await this.utilityService.runInSequenceAsList((jobs));
  }

  private extractItemIds(items: IDisplayItem[]): string[] {
    const itemIds = [];

    items.forEach((item: IDisplayItem) => {
      itemIds.push(item.collection_id + ':' + item.id);
    });

    return itemIds;
  }

  copyBibTexToClipboard(items: IItem[]) {
    const text = this.getItemBibTex(items);
    this.screenService.copyToClipboard(text, text);
  }

  copyRisToClipboard(items: IItem[]) {
    const text = this.getItemRis(items);
    this.screenService.copyToClipboard(text, text);
  }

  private getItemBibTex(items: IItem[]): string {
    const arr = items.map(item => asBib({ item }));
    return arr.join('\n\r');
  }

  private getItemRis(items: IItem[]): string {
    const arr = items.map(item => asRis({ item }));
    return arr.join('\n\r');
  }
}